import type { TokenPath } from '../tokens/generated/types'
import type { NamedColorDefinition, TokenBadgeColor } from './types'

const dataVizPrimary = (index: number): NamedColorDefinition => {
  const fill = `dataViz.primary.${index}.fill.default` as TokenPath
  const lighter = `dataViz.primary.${index}.border.subtle` as TokenPath
  const darker = `dataViz.primary.${index}.border.default` as TokenPath

  return {
    fill,
    border: { lighter, darker }
  }
}

const dataVizSecondary = (index: number): NamedColorDefinition => {
  const fill = `dataViz.secondary.${index}.fill.default` as TokenPath
  const lighter = `dataViz.secondary.${index}.border.subtle` as TokenPath
  const darker = `dataViz.secondary.${index}.border.default` as TokenPath

  return {
    fill,
    border: { lighter, darker }
  }
}

const dataVizSemantic = (name: 'error' | 'warning' | 'success' | 'info'): NamedColorDefinition => {
  const fill = `dataViz.semantic.${name}.fill.default` as TokenPath
  const darker = `dataViz.semantic.${name}.border.default` as TokenPath
  const lighter = name === 'info' ? (`dataViz.semantic.${name}.border.halo` as TokenPath) : darker

  return {
    fill,
    border: { lighter, darker }
  }
}

export const namedColorRegistry: Record<TokenBadgeColor, NamedColorDefinition> = {
  'precis-blue': dataVizPrimary(1),
  teal: dataVizPrimary(2),
  orange: dataVizPrimary(3),
  magenta: dataVizPrimary(4),
  violet: dataVizPrimary(5),
  andesite: dataVizPrimary(6),
  cobalt: dataVizPrimary(7),
  brown: dataVizPrimary(8),
  'graph-pink': dataVizSecondary(1),
  olive: dataVizSecondary(2),
  sienna: dataVizSecondary(3),
  'graph-green': dataVizSecondary(4),
  caramel: dataVizSecondary(5),
  aqua: dataVizSecondary(6),
  salmon: dataVizSecondary(7),
  brass: dataVizSecondary(8),
  'semantic-error': dataVizSemantic('error'),
  'semantic-warning': dataVizSemantic('warning'),
  'semantic-success': dataVizSemantic('success'),
  'semantic-info': dataVizSemantic('info'),
  neutral: {
    fill: 'foreground.semantic.neutral.default',
    border: {
      lighter: 'border.main.tonal',
      darker: 'foreground.main.secondary'
    }
  }
}

/** Stable product order for named colors — not alphabetical. */
export const namedColorIds = [
  'precis-blue',
  'teal',
  'orange',
  'magenta',
  'violet',
  'andesite',
  'cobalt',
  'brown',
  'graph-pink',
  'olive',
  'sienna',
  'graph-green',
  'caramel',
  'aqua',
  'salmon',
  'brass',
  'semantic-error',
  'semantic-warning',
  'semantic-success',
  'semantic-info',
  'neutral'
] as const satisfies readonly TokenBadgeColor[]

export const selectableNamedColors = namedColorIds

export const comparisonNamedColorRegistry = {
  'comparison-positive': {
    fill: 'foreground.semantic.success.default',
    border: {
      lighter: 'foreground.semantic.success.default',
      darker: 'foreground.semantic.success.default'
    }
  },
  'comparison-negative': {
    fill: 'foreground.semantic.error.default',
    border: {
      lighter: 'foreground.semantic.error.default',
      darker: 'foreground.semantic.error.default'
    }
  },
  'comparison-neutral': {
    fill: 'foreground.semantic.neutral.default',
    border: {
      lighter: 'foreground.semantic.neutral.default',
      darker: 'foreground.semantic.neutral.default'
    }
  }
} as const satisfies Record<string, NamedColorDefinition>
